import { Type, type Static } from "@sinclair/typebox";

export const FicOperationalDocumentTypeSchema = Type.Union([
  Type.Literal("work_report"),
  Type.Literal("proforma"),
]);

export const FicJobDocumentPreviewQuerySchema = Type.Object(
  {
    type: FicOperationalDocumentTypeSchema,
    date: Type.Optional(Type.String({ format: "date" })),
  },
  { additionalProperties: false },
);

export const FicJobDocumentCreateSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    type: FicOperationalDocumentTypeSchema,
    date: Type.String({ format: "date" }),
    confirmed: Type.Literal(true),
  },
  { additionalProperties: false },
);

export const BillingQuerySchema = Type.Object(
  {
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2200, default: 2026 }),
    ),
    status: Type.Optional(
      Type.Union([
        Type.Literal("to-invoice"),
        Type.Literal("ready"),
        Type.Literal("verification-required"),
        Type.Literal("failed"),
        Type.Literal("invoiced"),
        Type.Literal("archived"),
      ]),
    ),
    search: Type.Optional(Type.String({ maxLength: 200 })),
  },
  { additionalProperties: false },
);

export const BillingDocumentParamsSchema = Type.Object(
  { documentId: Type.String({ format: "uuid" }) },
  { additionalProperties: false },
);

export const BillingArchivePatchSchema = Type.Object(
  { archived: Type.Boolean() },
  { additionalProperties: false },
);

export const BillingInvoiceCreateSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    date: Type.String({ format: "date" }),
    // Tax fields are mandatory in the service for NEW invoices only. Older
    // pending operations can still be reconciled without changing their tax.
    vatId: Type.Optional(Type.Integer({ minimum: 1 })),
    vatPercent: Type.Optional(Type.Number({ minimum: 0, maximum: 100 })),
    withholdingEnabled: Type.Optional(Type.Boolean()),
    confirmed: Type.Literal(true),
  },
  { additionalProperties: false },
);

export const FicAutomationSettingsPatchSchema = Type.Object(
  {
    quoteSyncEnabled: Type.Boolean(),
    startupCheckEnabled: Type.Boolean(),
    intervalMinutes: Type.Integer({ minimum: 5, maximum: 1440 }),
  },
  { additionalProperties: false },
);

export type FicOperationalDocumentType = Static<
  typeof FicOperationalDocumentTypeSchema
>;
export type FicJobDocumentPreviewQuery = Static<
  typeof FicJobDocumentPreviewQuerySchema
>;
export type FicJobDocumentCreate = Static<typeof FicJobDocumentCreateSchema>;
export type BillingQuery = Static<typeof BillingQuerySchema>;
export type BillingArchivePatch = Static<typeof BillingArchivePatchSchema>;
export type BillingInvoiceCreate = Static<typeof BillingInvoiceCreateSchema>;
export type FicAutomationSettingsPatch = Static<
  typeof FicAutomationSettingsPatchSchema
>;

export interface FicDocumentPreviewIssue {
  code: string;
  message: string;
}

export interface FicActiveDocumentOperation {
  operationId: string;
  status: "pending" | "verification-required";
  date: string | null;
  vatId?: number | null;
  vatPercent?: string | null;
  withholdingEnabled?: boolean | null;
}

export interface FicJobDocumentPreview {
  mode: "preview";
  remoteWriteEnabled: boolean;
  ready: boolean;
  requiredScope: string;
  job: { id: string; code: string; title: string };
  company: {
    localId: string;
    externalId: string;
    name: string;
    connectionReady: boolean;
  } | null;
  client: {
    localId: string;
    externalId: string | null;
    name: string;
    vatNumber: string | null;
    taxCode: string | null;
  } | null;
  existingReport: {
    externalId: string | null;
    number: string | null;
    billingStatus: string;
  } | null;
  document: {
    type: FicOperationalDocumentType;
    date: string;
    subject: string;
    notes: string;
    revenueCenter: string | null;
    vatId: number | null;
    vatPercent: string | null;
    amountNet: string;
    items: Array<{
      description: string;
      quantity: "1.00";
      unit: "nr";
      netPrice: string;
    }>;
  };
  activeOperation: FicActiveDocumentOperation | null;
  blockers: FicDocumentPreviewIssue[];
  warnings: FicDocumentPreviewIssue[];
}

export interface FicDocumentOperationResult {
  operationId: string;
  operationStatus: "pending" | "succeeded" | "verification-required" | "failed";
  documentId: string | null;
  remoteDocumentId: string | null;
  remoteDocumentNumber: string | null;
  replayed: boolean;
}

export interface BillingItem {
  jobId: string;
  jobCode: string;
  jobTitle: string;
  clientName: string | null;
  sourceDocumentId: string | null;
  sourceDocumentType: "work_report" | "proforma" | null;
  sourceDocumentNumber: string | null;
  sourceRemoteId: string | null;
  localAmount: string | null;
  ficAmount: string | null;
  difference: string | null;
  status:
    | "missing-document"
    | "ready"
    | "verification-required"
    | "failed"
    | "invoiced"
    | "archived";
  invoiceDocumentId: string | null;
  invoiceNumber: string | null;
  invoiceRemoteId: string | null;
  updatedAt: string;
}

export interface BillingPage {
  managementYear: number;
  items: BillingItem[];
  summary: {
    missingDocument: number;
    ready: number;
    verificationRequired: number;
    failed: number;
    invoiced: number;
    archived: number;
  };
}

export interface BillingInvoicePreview {
  ready: boolean;
  remoteWriteEnabled: boolean;
  requiredScope: "issued_documents.invoices:a";
  source: {
    id: string;
    type: "work_report" | "proforma";
    remoteId: string;
    number: string | null;
    amountNet: string;
  };
  job: { id: string; code: string; title: string };
  invoice: {
    type: "invoice";
    date: string;
    electronic: false;
    keepSourceDocument: true;
    amountNet: string;
    vatId: number | null;
    vatPercent: string | null;
    withholdingEnabled: boolean;
    withholdingPercent: "4.00";
  };
  vatOptions: Array<{
    id: number;
    value: string;
    description: string;
    isDefault: boolean;
  }>;
  activeOperation: FicActiveDocumentOperation | null;
  blockers: FicDocumentPreviewIssue[];
}

export interface FicAutomationSettings {
  companyId: string | null;
  quoteSyncEnabled: boolean;
  startupCheckEnabled: boolean;
  intervalMinutes: number;
  executionState: "idle" | "running" | "failed";
  lastStartedAt: string | null;
  lastFinishedAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  nextRunAt: string | null;
}
